import React, { useState, useEffect } from 'react';
import { FaDollarSign, FaShoppingCart, FaBoxes, FaChartLine } from 'react-icons/fa';
import api from '../services/api';
import MetricCard from '../components/Dashboard/MetricCard';
import ChartCard from '../components/Dashboard/ChartCard';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ErrorMessage from '../components/Common/ErrorMessage';

const Dashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboard(response.data.data);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Dashboard load error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load dashboard. Please try again.';
      setError(errorMessage);
      // Set empty dashboard data to prevent undefined errors
      setDashboard({
        sales: { last7Days: 0, last30Days: 0, last90Days: 0 },
        orders: { last30Days: 0 },
        products: { active: 0 },
        averageOrderValue: 0,
        recentOrders: [],
        topProducts: [],
        alerts: []
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading dashboard..." />;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <ErrorMessage message={error} onClose={() => setError('')} />
        <div className="text-center">
          <button
            onClick={loadDashboard}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            🔄 Retry Loading Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const salesChartData = [
    { name: '7 days ago', sales: dashboard?.sales?.last7Days || 0 },
    { name: '30 days ago', sales: dashboard?.sales?.last30Days || 0 },
    { name: '90 days ago', sales: dashboard?.sales?.last90Days || 0 }
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>

      {/* Alerts */}
      {dashboard?.alerts?.length > 0 && (
        <div className="space-y-2">
          {dashboard.alerts.map((alert, index) => (
            <div
              key={index}
              className={`px-4 py-3 rounded ${
                alert.type === 'warning'
                  ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                  : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}
            >
              {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Sales (30 days)"
          value={`$${dashboard?.sales?.last30Days?.toFixed(2) || '0.00'}`}
          icon={FaDollarSign}
          color="green"
          trend="up"
          trendValue="+12.5%"
        />
        <MetricCard
          title="Orders (30 days)"
          value={dashboard?.orders?.last30Days || 0}
          icon={FaShoppingCart}
          color="blue"
          trend="up"
          trendValue="+8.3%"
        />
        <MetricCard
          title="Active Products"
          value={dashboard?.products?.active || 0}
          icon={FaBoxes}
          color="purple"
        />
        <MetricCard
          title="Avg Order Value"
          value={`$${dashboard?.averageOrderValue?.toFixed(2) || '0.00'}`}
          icon={FaChartLine}
          color="orange"
          trend="up"
          trendValue="+5.2%"
        />
      </div>

      {/* Sales Chart */}
      <ChartCard
        title="Sales Overview"
        data={salesChartData}
        type="line"
        dataKey="sales"
        xAxisKey="name"
        color="#10B981"
      />

      {/* Recent Orders & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h2>
          <div className="space-y-3">
            {dashboard?.recentOrders?.length > 0 ? (
              dashboard.recentOrders.map((order) => (
                <div key={order.id} className="flex items-center justify-between py-2 border-b">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{order.amazonOrderId}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(order.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">${order.total}</p>
                    <p className="text-xs text-gray-500">{order.status}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No recent orders</p>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Products</h2>
          <div className="space-y-3">
            {dashboard?.topProducts?.length > 0 ? (
              dashboard.topProducts.map((product) => (
                <div key={product.asin} className="flex items-center justify-between py-2 border-b">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                    <p className="text-xs text-gray-500">{product.units} units sold</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900 ml-4">
                    ${product.sales?.toFixed(2)}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No sales data</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
